/* ============================================================
   BLUEWATER SECURITY ACADEMY — Animation Engine
   Drop into: assets/bluewater-animations.js
   Load in theme.liquid:
   <script src="{{ 'bluewater-animations.js' | asset_url }}" defer></script>

   Provides:
   1. Scroll reveals  (.bsa-reveal and [data-stagger])
   2. Stat count-up   (.bsa-stats__num[data-count])
   3. Timeline draw   (.bsa-timeline)
   4. Anchor nav active state (.bsa-anchor-nav)
   5. Sticky register bar show/hide (.bsa-register-bar)
   6. Soft page-fade transitions on internal links
   All effects are skipped if the user prefers reduced motion.
   ============================================================ */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. Scroll reveals ---------- */
  function initReveals() {
    var targets = document.querySelectorAll('.bsa-reveal, [data-stagger]');
    if (!targets.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
    targets.forEach(function (el) { io.observe(el); });
  }

  /* ---------- 2. Stat count-up ---------- */
  function animateCount(el) {
    var target = parseFloat(el.dataset.count);
    var suffix = el.dataset.suffix || '';
    var duration = 1400;
    var start = null;

    function step(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / duration, 1);
      var eased = 1 - Math.pow(1 - p, 3); /* easeOutCubic */
      el.textContent = Math.round(target * eased) + suffix;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function initCounters() {
    var nums = document.querySelectorAll('.bsa-stats__num[data-count]');
    if (!nums.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      nums.forEach(function (el) {
        el.textContent = el.dataset.count + (el.dataset.suffix || '');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          animateCount(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    nums.forEach(function (el) { io.observe(el); });
  }

  /* ---------- 3. Learning-path timeline draw ---------- */
  function initTimeline() {
    var tl = document.querySelector('.bsa-timeline');
    if (!tl || reduced) return;
    var bar = tl.querySelector('.bsa-timeline__progress');
    if (!bar) return;

    var ticking = false;
    function update() {
      var rect = tl.getBoundingClientRect();
      var vh = window.innerHeight;
      var progress = (vh * 0.7 - rect.top) / rect.height;
      progress = Math.max(0, Math.min(1, progress));
      bar.style.setProperty('--bsa-progress', progress.toFixed(3));
      bar.style.transform = 'scaleY(' + progress.toFixed(3) + ')';
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------- 4. Anchor nav active state ---------- */
  function initAnchorNav() {
    var nav = document.querySelector('.bsa-anchor-nav');
    if (!nav) return;
    var links = Array.prototype.slice.call(nav.querySelectorAll('a[href^="#"]'));
    var sections = links
      .map(function (a) { return document.querySelector(a.getAttribute('href')); })
      .filter(Boolean);
    if (!sections.length || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          links.forEach(function (a) {
            a.classList.toggle('is-active',
              a.getAttribute('href') === '#' + entry.target.id);
          });
        }
      });
    }, { rootMargin: '-30% 0px -60% 0px' });
    sections.forEach(function (s) { io.observe(s); });
  }

  /* ---------- 5. Sticky mobile register bar ---------- */
  function initRegisterBar() {
    var bar = document.querySelector('.bsa-register-bar');
    if (!bar) return;
    var hero = document.querySelector('.bsa-hero');
    function toggle() {
      var past = hero ? hero.getBoundingClientRect().bottom < 0 : window.scrollY > 500;
      bar.classList.toggle('is-shown', past);
    }
    window.addEventListener('scroll', toggle, { passive: true });
    toggle();
  }

  /* ---------- 6. Soft page-fade transitions ---------- */
  function initPageTransitions() {
    if (reduced) return;
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a');
      if (!a) return;
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#' || a.target === '_blank' ||
          a.hasAttribute('download') || e.metaKey || e.ctrlKey || e.shiftKey) return;
      var url;
      try { url = new URL(href, location.href); } catch (err) { return; }
      if (url.origin !== location.origin) return;
      if (url.pathname === location.pathname && url.hash) return;

      e.preventDefault();
      document.body.classList.add('bsa-page-leave');
      setTimeout(function () { location.href = url.href; }, 150);
    });
    /* Restore opacity on back/forward cache */
    window.addEventListener('pageshow', function () {
      document.body.classList.remove('bsa-page-leave');
    });
  }

  function init() {
    initReveals();
    initCounters();
    initTimeline();
    initAnchorNav();
    initRegisterBar();
    initPageTransitions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init reveals when Shopify theme editor reloads sections */
  document.addEventListener('shopify:section:load', initReveals);
})();
